import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandMark } from '../components/Brand.js';
import { api } from '../lib/api.js';
import { setSession } from '../lib/auth.js';

// Login: email + password → SMS OTP (2FA) → session. Matches the design system (serif display,
// teal glow, warm surfaces). Never displays which step failed beyond a generic message. No secret
// is stored client-side beyond the tokens the API returns after successful 2FA.

const wrap: React.CSSProperties = {
  minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24,
  background: 'var(--bg)', backgroundImage: 'var(--canvas-tint)',
};
const card: React.CSSProperties = {
  width: '100%', maxWidth: 420, background: 'var(--surface-2)', border: '1px solid var(--hair)',
  borderRadius: 'var(--r-card)', boxShadow: 'var(--sh-hero)', padding: 32,
};
const label: React.CSSProperties = { fontSize: 13, color: 'var(--text-dim)', marginBottom: 8, fontWeight: 600, display: 'block' };
const input: React.CSSProperties = {
  width: '100%', height: 46, padding: '0 14px', borderRadius: 'var(--pill)', border: '1px solid var(--hair)',
  background: 'var(--surface-1)', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 15,
};

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState<'password' | 'otp'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [phoneHint, setPhoneHint] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitPassword(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await api.login(email.trim(), password);
      setChallengeId(r.challengeId);
      setPhoneHint(r.phoneHint);
      setStep('otp');
    } catch {
      setError('אימייל או סיסמה שגויים');
    } finally {
      setBusy(false);
    }
  }

  async function submitOtp(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const s = await api.verifyOtp(challengeId, code.trim());
      setSession(s.accessToken, s.refreshToken, s.user);
      navigate('/', { replace: true });
    } catch {
      setError('הקוד שגוי או פג תוקף');
    } finally {
      setBusy(false);
    }
  }

  async function resend(): Promise<void> {
    setError(null);
    try {
      await api.resendOtp(challengeId);
    } catch {
      setError('שליחת קוד חוזר נכשלה — נסה שוב בעוד רגע');
    }
  }

  return (
    <div style={wrap} dir="rtl">
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <BrandMark size={40} />
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, letterSpacing: 'var(--display-track)' }}>
            Cross<b>Castr</b>
          </div>
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, margin: '18px 0 6px' }}>
          {step === 'password' ? 'כניסה לחשבון' : 'אימות דו-שלבי'}
        </h1>
        <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 22 }}>
          {step === 'password'
            ? 'התחבר עם האימייל והסיסמה שלך'
            : `שלחנו קוד בן 6 ספרות למספר שמסתיים ב-${phoneHint}`}
        </p>

        {step === 'password' ? (
          <form onSubmit={submitPassword}>
            <label style={label} htmlFor="email">אימייל</label>
            <input id="email" style={{ ...input, marginBottom: 16 }} type="email" dir="ltr" autoComplete="username"
              value={email} onChange={(e) => setEmail(e.target.value)} required />
            <label style={label} htmlFor="password">סיסמה</label>
            <input id="password" style={{ ...input, marginBottom: 22 }} type="password" dir="ltr" autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)} required />
            <button className="btn" type="submit" disabled={busy || !email || !password} style={{ width: '100%', height: 46 }}>
              {busy ? 'מתחבר…' : 'המשך'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitOtp}>
            <label style={label} htmlFor="code">קוד אימות</label>
            <input id="code" style={{ ...input, marginBottom: 22, letterSpacing: 8, textAlign: 'center', fontFamily: 'var(--font-mono)' }}
              type="text" inputMode="numeric" dir="ltr" autoComplete="one-time-code" maxLength={6} placeholder="000000"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required />
            <button className="btn" type="submit" disabled={busy || code.length !== 6} style={{ width: '100%', height: 46 }}>
              {busy ? 'מאמת…' : 'התחבר'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, fontSize: 13 }}>
              <button type="button" className="btn ghost" onClick={resend} style={{ padding: '6px 12px' }}>שלח קוד חדש</button>
              <button type="button" className="btn ghost" onClick={() => { setStep('password'); setCode(''); setError(null); }} style={{ padding: '6px 12px' }}>
                חזרה
              </button>
            </div>
          </form>
        )}

        {error && (
          <div style={{ marginTop: 18, padding: '10px 14px', borderRadius: 'var(--r-sm)', background: 'color-mix(in srgb, var(--risk) 12%, transparent)', color: 'var(--risk)', fontSize: 14 }}>
            {error}
          </div>
        )}
        <div style={{ marginTop: 22, textAlign: 'center', fontSize: 12, color: 'var(--text-dim)' }}>
          <a href="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>מדיניות פרטיות</a>
          <span style={{ margin: '0 8px', opacity: 0.5 }}>·</span>
          <a href="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>תקנון</a>
        </div>
      </div>
    </div>
  );
}
